import React from "react";
import { Link } from "react-router-dom";
import { Download, ArrowRight, Target, TrendingUp, Map, Layers } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { INVESTOR_HIGHLIGHTS, ROADMAP } from "@/lib/data";
import { openExternal } from "@/lib/openExternal";

const PITCH_DECK = "/downloads/tratou-pitch-deck.pdf";

export default function Investors() {
  return (
    <Layout>
      <section className="py-28 border-b border-white/10" data-testid="investors-hero">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <span className="eyebrow">Investor Relations</span>
          <h1 className="heading-massive mt-4">
            Backing the <span className="text-tratou-green">post-diesel</span> economy.
          </h1>
          <p className="mt-8 max-w-2xl text-tratou-dim text-lg">
            Tratou Energy is building the world's largest fleet of Mobile Battery Energy Storage Systems —
            an Energy-as-a-Service platform replacing the 1.4 TWh / year diesel-on-site economy.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href={PITCH_DECK} target="_blank" rel="noreferrer" onClick={(e) => openExternal(e, PITCH_DECK)} className="btn-primary" data-testid="investors-deck">
              <Download className="w-4 h-4" /> Download Pitch Deck
            </a>
            <Link to="/contact?intent=investor" className="btn-secondary" data-testid="investors-contact">
              Talk to Founders <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-24" data-testid="investors-mission">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-3 gap-12">
          <Card icon={Target} title="Mission" body="Provide electricity where the grid cannot reach by using Mobile Battery Energy Storage Systems and Energy-as-a-Service contracts." />
          <Card icon={Map} title="Market" body="A $280B addressable market by 2030. Diesel-on-site spend exceeds $42B/yr globally. ESG mandates accelerate the shift." />
          <Card icon={Layers} title="Model" body="EaaS subscription + asset-light fleet operations. Recurring revenue, asset utilisation north of 80%, gross margin 55%+." />
        </div>
      </section>

      {/* Highlights */}
      <section className="py-24 border-y border-white/10" data-testid="investors-highlights">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <span className="eyebrow">Financial Highlights</span>
          <h2 className="heading-section mt-4">The numbers.</h2>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {INVESTOR_HIGHLIGHTS.map((h) => (
              <div key={h.k} className="bg-tratou-deep p-6">
                <p className="eyebrow-dim">{h.k}</p>
                <p className="mt-3 font-display text-3xl font-bold text-tratou-green">{h.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roadmap */}
      <section className="py-24" data-testid="investors-roadmap">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <span className="eyebrow">Roadmap</span>
          <h2 className="heading-section mt-4">From pilot to <span className="text-tratou-green">1 GWh</span>.</h2>
          <div className="mt-12 relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-white/10" />
            <div className="space-y-12">
              {ROADMAP.map((r, i) => (
                <div key={r.year} className={`relative md:grid md:grid-cols-2 md:gap-12 ${i % 2 ? "md:[direction:rtl]" : ""}`}>
                  <div className={`pl-12 md:pl-0 ${i % 2 ? "md:[direction:ltr] md:text-left" : "md:text-right"}`}>
                    <span className="absolute left-2.5 md:left-1/2 top-2 -translate-x-1/2 w-3 h-3 bg-tratou-green border border-tratou-void" />
                    <p className="font-mono text-tratou-green text-sm tracking-[0.2em]">{r.year}</p>
                    <p className="mt-2 font-display text-xl font-bold">{r.milestone}</p>
                  </div>
                  <div />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 border-t border-white/10 text-center" data-testid="investors-cta">
        <div className="max-w-2xl mx-auto px-6">
          <span className="eyebrow">Series A — Open</span>
          <h2 className="heading-section mt-4">Let's build the post-diesel grid.</h2>
          <p className="mt-4 text-tratou-dim">
            We're raising $25M to scale our MBESS fleet across MENA and South Asia.
          </p>
          <Link to="/contact?intent=investor" className="btn-primary mt-8" data-testid="investors-cta-talk">
            Request Data Room Access <ArrowRight className="w-4 h-4" />
          </Link>
          <a
            href={PITCH_DECK}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => openExternal(e, PITCH_DECK)}
            className="btn-secondary mt-4 sm:mt-8 sm:ml-3 inline-flex"
            data-testid="investors-cta-deck"
          >
            <Download className="w-4 h-4" /> Download Pitch Deck
          </a>
        </div>
      </section>
    </Layout>
  );
}

function Card({ icon: Icon, title, body }) {
  return (
    <div className="panel p-8 panel-hover">
      <Icon className="w-7 h-7 text-tratou-green mb-5" />
      <h3 className="font-display font-bold text-xl">{title}</h3>
      <p className="mt-3 text-tratou-dim leading-relaxed">{body}</p>
    </div>
  );
}
